/**
 * Shared cache for sprite alpha data to enable efficient pixel-perfect hit detection
 * across multiple sprite instances using the same texture.
 */

class SpriteAlphaDataCache
{
  constructor()
  {
    this.cache = new Map(); // imageSrc -> { data: Uint8Array, width: number, height: number }
    this.processing = new Map(); // imageSrc -> Promise (to avoid duplicate processing)

    // Reusable canvas and context for better performance
    this.canvas = null;
    this.ctx = null;
  }

  /**
   * Pre-warm the cache with commonly used sprites at game start
   * @param {string[]} imageSrcs - Array of image source URLs to pre-process
   * @returns {Promise<void>}
   */
  async preWarmCache( imageSrcs )
  {
    // console.log( `Pre-warming alpha data cache for ${imageSrcs.length} sprites...` );

    const promises = imageSrcs.map( imageSrc => this.getAlphaData( imageSrc ) );
    await Promise.all( promises );

    // console.log( `Alpha data cache pre-warmed for ${this.cache.size} sprites` );
  }

  /**
   * Get alpha data for a given image source, processing if needed
   * @param {string} imageSrc - The image source URL
   * @returns {Promise<{data: Uint8Array, width: number, height: number} | null>}
   */
  async getAlphaData( imageSrc )
  {
    // Return cached data if available
    if( this.cache.has( imageSrc ) )
    {
      return this.cache.get( imageSrc );
    }

    // Return existing processing promise if already in progress
    if( this.processing.has( imageSrc ) )
    {
      return this.processing.get( imageSrc );
    }

    // Start processing
    const processingPromise = this._processAlphaData( imageSrc );
    this.processing.set( imageSrc, processingPromise );

    try
    {
      const result = await processingPromise;
      this.cache.set( imageSrc, result );
      return result;
    } catch( error )
    {
      console.warn( `Failed to process alpha data for ${imageSrc}:`, error );
      return null;
    } finally
    {
      this.processing.delete( imageSrc );
    }
  }

  /**
   * Get alpha data synchronously if already cached
   * @param {string} imageSrc - The image source URL
   * @returns {{data: Uint8Array, width: number, height: number} | null}
   */
  getCachedAlphaData( imageSrc )
  {
    return this.cache.get( imageSrc ) || null;
  }

  /**
   * Check if alpha data is cached for a given image source
   * @param {string} imageSrc - The image source URL
   * @returns {boolean}
   */
  hasCachedAlphaData( imageSrc )
  {
    return this.cache.has( imageSrc );
  }

  /**
   * Clear the entire cache (useful for memory management)
   */
  clearCache()
  {
    this.cache.clear();
    this.processing.clear();

    // Clean up canvas resources
    if( this.canvas )
    {
      this.canvas.width = 0;
      this.canvas.height = 0;
      this.canvas = null;
      this.ctx = null;
    }
  }

  /**
   * Remove specific entry from cache
   * @param {string} imageSrc - The image source URL to remove
   */
  removeCachedAlphaData( imageSrc )
  {
    this.cache.delete( imageSrc );
  }

  /**
   * Get cache statistics
   * @returns {{cacheSize: number, processingCount: number, memoryUsage: string}}
   */
  getStats()
  {
    let totalPixels = 0;
    for( const alphaData of this.cache.values() )
    {
      if( alphaData )
      {
        totalPixels += alphaData.width * alphaData.height;
      }
    }

    return {
      cacheSize: this.cache.size,
      processingCount: this.processing.size,
      memoryUsage: `~${Math.round( totalPixels / 1024 )} KB`
    };
  }

  /**
   * Get or create reusable canvas context
   * @private
   */
  _getCanvasContext()
  {
    if( !this.canvas )
    {
      this.canvas = document.createElement( 'canvas' );
      this.ctx = this.canvas.getContext( '2d', { willReadFrequently: true } );

      if( !this.ctx )
      {
        throw new Error( 'Failed to get canvas 2d context' );
      }
    }

    return { canvas: this.canvas, ctx: this.ctx };
  }

  /**
   * Internal method to process alpha data from an image source
   * @private
   */
  async _processAlphaData( imageSrc )
  {
    return new Promise( ( resolve, reject ) =>
    {
      const img = new Image();

      // Set up timeout to prevent hanging
      const timeout = setTimeout( () =>
      {
        reject( new Error( `Timeout loading image: ${imageSrc}` ) );
      }, 10000 ); // 10 second timeout

      img.onload = () =>
      {
        clearTimeout( timeout );
        try
        {
          // Validate image dimensions
          if( img.width === 0 || img.height === 0 )
          {
            reject( new Error( `Invalid image dimensions: ${img.width}x${img.height} for ${imageSrc}` ) );
            return;
          }

          // Use reusable canvas context for better performance
          const { canvas, ctx } = this._getCanvasContext();

          // Resize canvas to match image dimensions
          canvas.width = img.width;
          canvas.height = img.height;

          // Clear any previous content and draw the image
          ctx.clearRect( 0, 0, img.width, img.height );
          ctx.drawImage( img, 0, 0 );
          const imageData = ctx.getImageData( 0, 0, img.width, img.height );

          // Validate image data
          if( !imageData || !imageData.data || imageData.data.length === 0 )
          {
            reject( new Error( `Failed to get image data for ${imageSrc}` ) );
            return;
          }

          // Extract only alpha channel data for memory efficiency
          const alphaArray = new Uint8Array( img.width * img.height );
          for( let i = 0; i < imageData.data.length; i += 4 )
          {
            alphaArray[ i / 4 ] = imageData.data[ i + 3 ]; // Store alpha values (0-255)
          }

          resolve( {
            data: alphaArray,
            width: img.width,
            height: img.height
          } );
        } catch( error )
        {
          reject( new Error( `Failed to process alpha data for ${imageSrc}: ${error.message}` ) );
        }
      };

      img.onerror = ( event ) =>
      {
        clearTimeout( timeout );
        reject( new Error( `Failed to load image: ${imageSrc} - ${event.message || 'Unknown error'}` ) );
      };

      // Handle CORS if needed
      img.crossOrigin = 'anonymous';

      try
      {
        img.src = imageSrc;
      } catch( error )
      {
        clearTimeout( timeout );
        reject( new Error( `Failed to set image source: ${imageSrc} - ${error.message}` ) );
      }
    } );
  }
}

// Create a singleton instance
const spriteAlphaDataCache = new SpriteAlphaDataCache();

export default spriteAlphaDataCache;
